import type {
  Conversation,
  ConversationCategory,
  Horoscope,
  Message,
  Story,
  User,
} from "../models";
import { ZODIAC_SIGNS, zodiacIndex } from "../models";
import { dateOnly, daysFromToday } from "../mockDate";
import { CURRENT_USER_AUTHOR, authorForId } from "./people";

/**
 * Ports of `MockHoroscopeService`, `MockAuthService` and `MockMessagingService`.
 *
 * Messaging is carried over for completeness, but note the product decision:
 * there is no in-app inbox on this platform — Telegram *is* the inbox. What
 * survives here feeds the handoff (which conversation a match or study group
 * opens into) and the stories strip, not a chat UI.
 */

const delay = (seconds: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, seconds * 1000));

/* ---------------- Horoscope ---------------- */

interface Reading {
  headline: string;
  body: string;
  mood: string;
}

/** Twelve readings — one per sign on any given day. */
const READINGS: Reading[] = [
  {
    headline: "Say yes to the thing you keep rescheduling",
    body: "That plan you've moved three times finally has an opening today, and the version of you that shows up is better rested than you expect. Momentum is cheaper than motivation right now — start before you feel ready.",
    mood: "Restless",
  },
  {
    headline: "Your group project needs one honest sentence",
    body: "Somebody has to name what isn't working, and today you're the one with the clearest read on it. Say it plainly and early. The awkward ninety seconds buys back a week.",
    mood: "Direct",
  },
  {
    headline: "Go to office hours you don't strictly need",
    body: "The question you'd feel silly asking is the one that unlocks the rest of the unit. People remember the students who show up curious, not the ones who show up correct.",
    mood: "Curious",
  },
  {
    headline: "A quiet day is not a wasted one",
    body: "Your energy is better spent consolidating than expanding. Close open loops, answer the messages you've been leaving on read, and let tomorrow be the ambitious one.",
    mood: "Grounded",
  },
  {
    headline: "Someone's about to surprise you",
    body: "A person you'd filed under acquaintance says something that reframes them entirely. Stay in the conversation past the point where you'd normally leave — that's where it happens.",
    mood: "Open",
  },
  {
    headline: "Protect the first two hours",
    body: "Whatever you touch first today gets your sharpest thinking. Spend it on the hard thing, not the easy inbox. The rest of the day will be noisier than you'd like.",
    mood: "Focused",
  },
  {
    headline: "Stop optimizing and start finishing",
    body: "The system you're refining is already good enough. Done and slightly imperfect beats perfect and theoretical — especially this week, when the deadline is closer than your calendar suggests.",
    mood: "Decisive",
  },
  {
    headline: "Eat an actual meal with an actual person",
    body: "You've been treating food as fuel and friendship as scheduling. Both are running low. A real dinner with someone who isn't in your major does more for you today than another hour of review.",
    mood: "Warm",
  },
  {
    headline: "The comparison is not the assignment",
    body: "You're measuring your first draft against someone else's final one. Their pace is not your pace, and their advantage is mostly a head start you can't see. Run your own race today.",
    mood: "Steady",
  },
  {
    headline: "Ask for the extension",
    body: "The worst outcome is being told no, which leaves you exactly where you are. Most people who look effortless are simply better at asking. Today's a good day to practice.",
    mood: "Bold",
  },
  {
    headline: "Follow the tangent",
    body: "Something outside your requirements has your attention, and it isn't procrastination — it's signal. Give it an hour. The most useful thing you learn this semester probably isn't on a syllabus.",
    mood: "Inspired",
  },
  {
    headline: "Leave the room before you're drained",
    body: "You'll be tempted to stay until the energy runs all the way out. Don't. Leaving while you still have something left is what makes people glad to see you next time.",
    mood: "Measured",
  },
];

/** Days since epoch — stable within a calendar day, +1 at midnight. */
function dayIndex(): number {
  const now = new Date();
  return Math.floor(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / 86_400_000);
}

/**
 * Campus-flavoured daily horoscope.
 *
 * Readings are deterministic per (sign, day): the same sign shows the same
 * reading all day, every sign shows a different one, and the whole set rotates
 * at midnight. No randomness — a reload never changes what's on screen.
 */
export class MockHoroscopeService {
  async getDailyHoroscope(sign: string): Promise<Horoscope> {
    await delay(0.2);

    const day = dayIndex();
    const idx = zodiacIndex(sign);
    const reading = READINGS[(day + idx) % READINGS.length];

    // Compatible sign: deterministic, and never the reader's own.
    const count = ZODIAC_SIGNS.length;
    const offset = 1 + ((day + idx) % (count - 1));
    const compatible = ZODIAC_SIGNS[(idx + offset) % count];

    return {
      sign,
      date: dateOnly(0),
      headline: reading.headline,
      body: reading.body,
      mood: reading.mood,
      luckyNumber: 1 + ((day * 7 + idx * 3) % 99),
      compatibleSign: compatible,
    };
  }
}

/* ---------------- Auth ---------------- */

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  svacId: string;
}

/**
 * The signed-in student.
 *
 * `createdAt` is a relative offset. The Swift original pinned it to an absolute
 * date that has since fallen on the wrong side of "now", so the demo account
 * claimed to have been created in the future.
 */
export const DEMO_USER: User = {
  id: "user-001",
  primaryEmail: "alex@utexas.edu",
  displayName: "Alex Chen",
  profilePhotoURL: CURRENT_USER_AUTHOR.profilePhotoURL,
  school: "UT Austin",
  major: "Computer Science",
  year: "2029",
  isStudentVerified: true,
  froshPoints: 1250,
  vibeAnalysisText:
    "Creative explorer with a love for deep conversations and spontaneous adventures.",
  zodiacSign: "Sagittarius",
  createdAt: daysFromToday(-38, 10, 0),
};

export class MockAuthService {
  private user: User = { ...DEMO_USER };

  async sendVerificationCode(_email: string): Promise<void> {
    await delay(0.5);
  }

  async verifyCode(_email: string, _code: string): Promise<AuthResponse> {
    await delay(0.5);
    return {
      accessToken: `mock-access-token-${crypto.randomUUID()}`,
      refreshToken: `mock-refresh-token-${crypto.randomUUID()}`,
      tokenType: "Bearer",
      expiresIn: 3600,
      svacId: "user-001",
    };
  }

  async refreshToken(_refreshToken: string): Promise<AuthResponse> {
    await delay(0.3);
    return {
      accessToken: `mock-refreshed-token-${crypto.randomUUID()}`,
      refreshToken: `mock-refresh-token-${crypto.randomUUID()}`,
      tokenType: "Bearer",
      expiresIn: 3600,
      svacId: "user-001",
    };
  }

  async getCurrentUser(): Promise<User> {
    return this.user;
  }

  async updateProfile(patch: Partial<User>): Promise<User> {
    await delay(0.5);
    this.user = { ...this.user, ...patch };
    return this.user;
  }

  async logout(): Promise<void> {
    await delay(0.2);
  }
}

/* ---------------- Messaging ---------------- */

function participant(id: string) {
  const author = id === "user-001" ? CURRENT_USER_AUTHOR : authorForId(id);
  return {
    id: author.id,
    displayName: author.displayName,
    profilePhotoURL: author.profilePhotoURL,
  };
}

export class MockMessagingService {
  async getConversations(category?: ConversationCategory): Promise<Conversation[]> {
    await delay(0.3);
    const all: Conversation[] = [
      {
        id: "conv1",
        participants: [participant("user-001"), participant("u1")],
        lastMessage: {
          id: "msg-last-1",
          conversationId: "conv1",
          senderId: "u1",
          text: "See you in CS 101 tomorrow!",
          isRead: false,
          createdAt: daysFromToday(-1, 22, 30),
        },
        unreadCount: 1,
        category: "classmates",
        isGroup: false,
        createdAt: daysFromToday(-4, 10, 0),
      },
      {
        id: "conv2",
        participants: [participant("user-001"), participant("u2")],
        lastMessage: {
          id: "msg-last-2",
          conversationId: "conv2",
          senderId: "u2",
          text: "Omg we're a 92% match! That's insane",
          isRead: true,
          createdAt: daysFromToday(-1, 20, 15),
        },
        unreadCount: 0,
        category: "matches",
        isGroup: false,
        createdAt: daysFromToday(-3, 10, 0),
      },
      {
        id: "conv3",
        participants: [participant("user-001"), participant("u1"), participant("u3")],
        lastMessage: {
          id: "msg-last-3",
          conversationId: "conv3",
          senderId: "u3",
          text: "Who's going to office hours this week?",
          isRead: false,
          createdAt: daysFromToday(-1, 18, 0),
        },
        unreadCount: 3,
        category: "groups",
        isGroup: true,
        groupName: "CS 101 Study Squad",
        createdAt: daysFromToday(-18, 10, 0),
      },
    ];
    return category === undefined ? all : all.filter((c) => c.category === category);
  }

  async getMessages(conversationId: string): Promise<Message[]> {
    await delay(0.3);
    return [
      {
        id: "msg1",
        conversationId,
        senderId: "user-001",
        text: "Hey! How's studying going?",
        isRead: true,
        createdAt: daysFromToday(-1, 20, 0),
      },
      {
        id: "msg2",
        conversationId,
        senderId: "u1",
        text: "Good! Working on the CS assignment",
        isRead: true,
        createdAt: daysFromToday(-1, 20, 5),
      },
      {
        id: "msg3",
        conversationId,
        senderId: "user-001",
        text: "Same, the linked list part is tricky",
        isRead: true,
        createdAt: daysFromToday(-1, 20, 10),
      },
      {
        id: "msg4",
        conversationId,
        senderId: "u1",
        text: "See you in CS 101 tomorrow!",
        isRead: false,
        createdAt: daysFromToday(-1, 22, 30),
      },
    ];
  }

  async sendMessage(
    conversationId: string,
    text: string,
    media?: { image?: Blob; video?: Blob },
  ): Promise<Message> {
    await delay(media ? 0.3 : 0.2);
    return {
      id: `msg-${crypto.randomUUID()}`,
      conversationId,
      senderId: "user-001",
      text,
      imageURL: media?.image
        ? `https://cdn.freshman.app/messages/img-${crypto.randomUUID()}.jpg`
        : undefined,
      videoURL: media?.video
        ? `https://cdn.freshman.app/messages/vid-${crypto.randomUUID()}.mp4`
        : undefined,
      isRead: true,
      createdAt: daysFromToday(0, 12, 0),
    };
  }

  async findOrCreateConversation(participantId: string): Promise<Conversation> {
    await delay(0.3);
    return {
      id: `conv-${crypto.randomUUID()}`,
      participants: [participant("user-001"), participant(participantId)],
      unreadCount: 0,
      category: "other",
      isGroup: false,
      createdAt: daysFromToday(0, 12, 0),
    };
  }

  async createConversation(
    participantIds: string[],
    isGroup: boolean,
    groupName?: string,
  ): Promise<Conversation> {
    await delay(0.3);
    return {
      id: `conv-${crypto.randomUUID()}`,
      participants: participantIds.map(participant),
      unreadCount: 0,
      category: isGroup ? "groups" : "other",
      isGroup,
      groupName,
      createdAt: daysFromToday(0, 12, 0),
    };
  }

  async markAsRead(_conversationId: string): Promise<void> {
    await delay(0.1);
  }

  async getStories(): Promise<Story[]> {
    await delay(0.3);
    return [
      {
        id: "s1",
        author: authorForId("u1"),
        mediaURL: "https://cdn.freshman.app/stories/1.jpg",
        mediaType: "image",
        isViewed: false,
        expiresAt: daysFromToday(1, 10, 0),
        createdAt: daysFromToday(0, 10, 0),
      },
      {
        id: "s2",
        author: authorForId("u2"),
        mediaURL: "https://cdn.freshman.app/stories/2.mp4",
        mediaType: "video",
        isViewed: false,
        expiresAt: daysFromToday(1, 8, 0),
        createdAt: daysFromToday(0, 8, 0),
      },
      {
        id: "s3",
        author: authorForId("u5"),
        mediaURL: "https://cdn.freshman.app/stories/3.jpg",
        mediaType: "text",
        textContent: "Finals are coming!",
        styleTemplate: "gradient_blue",
        isViewed: true,
        expiresAt: daysFromToday(1, 6, 0),
        createdAt: daysFromToday(0, 6, 0),
      },
    ];
  }

  async createStory(input: {
    mediaURL: string;
    mediaType: string;
    textContent?: string;
    styleTemplate?: string;
  }): Promise<Story> {
    await delay(0.5);
    return {
      id: `s-${crypto.randomUUID()}`,
      author: CURRENT_USER_AUTHOR,
      ...input,
      isViewed: true,
      expiresAt: daysFromToday(1, 12, 0),
      createdAt: daysFromToday(0, 12, 0),
    };
  }
}
